/**
 * Mobile Search Drawer Component
 * Handles full-screen mobile search experience.
 * Extends BaseDrawer for drawer functionality and uses BaseSearch for search functionality.
 */

import BaseDrawer from "@stardust/base-drawer";
import { loadSearchCore } from "@stardust/search-loader";

// Event names for mobile search drawer
const MOBILE_SEARCH_EVENTS = {
  open: 'mobile-search:open',
  close: 'mobile-search:close',
};

/**
 * MobileSearchDrawer combines BaseDrawer (for drawer UI) with BaseSearch (for search logic).
 * We use composition for BaseSearch since we can only extend one class.
 */
class MobileSearchDrawer extends BaseDrawer {
  constructor(element) {
    super(element, {
      openClass: 'opacity-0 -translate-y-full md:translate-y-0 md:translate-x-full',
      closeClass: 'opacity-0 -translate-y-full md:translate-y-0 md:translate-x-full',
    });

    // BaseSearch instance — constructed lazily on first open (see
    // _ensureSearch), so the heavy @stardust/search graph never loads
    // without search intent.
    this._search = null;
    this._searchReady = null;

    this.searchInput = element.querySelector("[data-mobile-search-input]");
    this.searchForm = element.querySelector("[data-mobile-search-form]");
    this.clearButton = element.querySelector("[data-mobile-search-clear]");
    this._handleClearClick = this._handleClearClick.bind(this);

    // The header trigger shown in place of this drawer (before it's opened)
    // is a separate element — keep its text mirroring this input so closing
    // without searching doesn't leave it showing stale query text.
    this._triggerText = document.querySelector('[data-mobile-search-trigger-text]');
    this._syncTriggerText = this._syncTriggerText.bind(this);
  }

  /**
   * Lazily import the search core and wire a BaseSearch instance to the
   * drawer, memoized. First call kicks off the import; the drawer is already
   * open on server-rendered content (skeletons under SearchSpring) while it
   * loads.
   * @returns {Promise<BaseSearch | null>} null if the core failed to load
   *   (memo reset so the next open retries)
   * @private
   */
  _ensureSearch() {
    if (!this._searchReady) {
      this._searchReady = loadSearchCore().then((BaseSearch) => {
        if (!BaseSearch) {
          this._searchReady = null;
          return null;
        }
        const element = this.element;
        const search = new BaseSearch();
        search.searchInput = this.searchInput;
        search.searchesTitle = element.querySelector("[data-mobile-searches-title]");
        search.searchesList = element.querySelector("[data-mobile-searches-list]");
        search.searchesSection = element.querySelector("[data-mobile-searches]");
        search.productsTitle = element.querySelector("[data-mobile-products-title]");
        search.productsSection = element.querySelector("[data-mobile-products-section]");
        search.productsGrid = element.querySelector("[data-mobile-products-grid]");
        search.searchResultsStatus = element.querySelector("[data-mobile-search-results-status]");
        search.emptyState = element.querySelector("[data-mobile-empty-state]");
        search.emptyText = element.querySelector("[data-mobile-empty-text]");
        search.featuredProductsContainer = element.querySelector("[data-mobile-featured-products-container]");
        search.featuredProductsTitle = element.querySelector("[data-mobile-featured-products-title]");
        search.helpfulPagesSection = element.querySelector("[data-mobile-helpful-pages-section]");
        search.viewAllContainer = element.querySelector("[data-mobile-view-all]");
        search.viewAllLink = element.querySelector(".autocomplete__search-view-all-link");

        // Override getMaxProducts to use the data attribute (matches desktop-search.js)
        const trendingCount = parseInt(this.searchInput?.dataset.trendingCount, 10);
        if (trendingCount) {
          search.getMaxProducts = () => trendingCount;
        }

        // Popular-search / suggestion link clicks (search.js's
        // attachSearchLinkHandlers) set the input value directly then call
        // performSearch() — no native 'input' event fires for that path, so
        // the 'input' listener would miss it. Wrap the one choke point both
        // paths share so the header trigger text stays in sync.
        const performSearch = search.performSearch.bind(search);
        search.performSearch = (query) => {
          this._syncTriggerText();
          return performSearch(query);
        };

        search.initSearch();
        this._search = search;
        return search;
      });
    }
    return this._searchReady;
  }

  load() {
    // Listen for open event
    document.addEventListener(MOBILE_SEARCH_EVENTS.open, this._boundOpen);

    // Listen for close event
    document.addEventListener(MOBILE_SEARCH_EVENTS.close, this._boundClose);

    if (this.clearButton) {
      this.clearButton.addEventListener('click', this._handleClearClick);
    }

    this.searchInput?.addEventListener('input', this._syncTriggerText);

    // URL query (`?q=`) is synced into both the input and the trigger text by
    // search-loader.js, which loads ahead of this module — don't redo it here.
  }

  /**
   * Mirror the drawer's search input value onto the header trigger's text,
   * falling back to its default placeholder when the input is empty.
   */
  _syncTriggerText() {
    if (!this._triggerText) return;
    this._triggerText.textContent = this.searchInput?.value || this._triggerText.dataset.defaultText;
  }

  /**
   * Reflect the drawer state on the single row header's search icon.
   * @param {boolean} expanded
   */
  _setTriggersExpanded(expanded) {
    document.querySelectorAll('[data-mobile-search-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(expanded));
    });
  }

  onClose() {
    this._setTriggersExpanded(false);
  }

  // Hook called when drawer opens
  onOpen() {
    this._setTriggersExpanded(true);
    // Drawer is already open on server-rendered content; load the search core
    // behind it and sync with the input's current value (?q= prefill or
    // anything typed while it loaded).
    this._ensureSearch().then((search) => search?.syncSearch());
    // Focus search input - use rAF instead of setTimeout for better INP
    // The focus trap already handles initial focus, but we want to specifically
    // target the search input which may not be the first focusable element
    requestAnimationFrame(() => {
      if (this.searchInput) {
        this.searchInput.focus({ preventScroll: true });
        // Refocusing resets the caret to the start — since the value now
        // persists across close/reopen, put it back at the end.
        const end = this.searchInput.value.length;
        this.searchInput.setSelectionRange(end, end);
      }
    });
  }

  // Closing without searching keeps the input, query state, and trigger text
  // intact (matches desktop-search.js) — only the explicit clear button resets.

  _handleClearClick(event) {
    event.preventDefault();
    if (this._search) {
      this._search.clearSearch();
    } else if (this.searchInput) {
      // Core not loaded yet — just empty the field; syncSearch on load
      // restores default content from the same empty value.
      this.searchInput.value = '';
    }
    this._syncTriggerText();

    if (this.searchInput) {
      this.searchInput.focus({ preventScroll: true });
    }
  }
}

// Initialize mobile search drawer
const init = () => {
  const mobileSearchDrawer = document.querySelector('[data-mobile-search-drawer]');
  if (mobileSearchDrawer) new MobileSearchDrawer(mobileSearchDrawer).load();
};
init();
